import { map } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { QueryService } from './query.service';
import { MessageService } from '../../core/service/message.service';
import { EsriGraphic, EsriRequest } from '../../config/esri.module';
import { Observable, from } from 'rxjs';
import { CONSTANTS } from '../../config/constants.module';
import { ErrorHandlingService } from '../../core/service/error-handling.service';

/**
 * @copyright Schweizerische Bundesbahnen SBB
 * @class RelationshipService
 * @description The RelationshipService provides help-methods for relationships.
 */
@Injectable({
  providedIn: 'root',
})
export class RelationshipService {
  constructor(
    private _queryService: QueryService,
    private _messageService: MessageService,
    @Inject(CONSTANTS) private _CONST: any
  ) {}

  /**
   * @name getRelationshipInfoOfLayerInfo
   * @param layerInfo
   * @param relationshipName
   * @description Returns relationship-info depending on name
   */
  public getRelationshipInfoOfLayerInfo(layerInfo: any, relationshipName: string): any {
    return layerInfo.relationships.find(f => f.name.toLowerCase() === relationshipName.toLowerCase());
  }

  /**
   * @name queryRelatedFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param feature
   * @description Get data by feature from related table
   */
  public queryRelatedFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    feature: __esri.Graphic
  ): Observable<__esri.Graphic[]> {
    const objectId = feature.attributes.OBJECTID;
    const relatedQueryProp = {
      objectIds: [objectId],
      returnGeometry: false,
      relationshipId: relationshipInfo.id,
    };
    const relatedQueryParams = this._queryService.buildRelationshipQuery(relatedQueryProp);
    const errorHandling = this._CONST.LAYER_ERROR.GET_FEATURES_FROM_RELATION_TABLE;
    return this._queryService
      .executeRelationshipQuery(featureLayerUrl, relatedQueryParams, errorHandling)
      .pipe(
        map(results => {
          const result = results[objectId];
          return result ? result.features : [];
        })
      );
  }

  /**
   * @name queryRelationshipFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param relatedRelationshipInfo
   * @param feature
   * @param relatedFeature
   * @returns {Observable<any>}: result from Query. this includes the features and the field information
   * @description  finds the relationship feature (m-n table entry)
   */
  public queryRelationshipFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    relatedRelationshipInfo: any,
    feature: __esri.Graphic,
    relatedFeature: __esri.Graphic
  ): Observable<any> {
    const relationshipLayerUrl = this.getRelationshipLayerUrl(featureLayerUrl, relationshipInfo);

    // create where
    const fkField: string = relationshipInfo.keyFieldInRelationshipTable;
    const fkFieldValue = feature.attributes[relationshipInfo.keyField];
    const fkFieldRelated: string = relatedRelationshipInfo.keyFieldInRelationshipTable;
    const fkFieldRelatedValue: string = relatedFeature.attributes[relatedRelationshipInfo.keyField];
    const where: string =
      fkField + "='" + fkFieldValue + "' AND " + fkFieldRelated + "='" + fkFieldRelatedValue + "'";

    // create query
    const relationshipQuery: __esri.Query = this._queryService.buildQuery(false, where);
    const errorCode = this._CONST.LAYER_ERROR.GET_FEATURES_FROM_RELATION_TABLE;
    // execute query
    return this._queryService.execute(relationshipLayerUrl, relationshipQuery, errorCode);
  }

  /**
   * @name queryRelationshipCount
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param relatedRelationshipInfo
   * @param relatedFeature
   * @returns {Observable<any>}
   * @description  finds the relationship count (m-n table entries)
   */
  public queryRelationshipCount(
    featureLayerUrl: string,
    relationshipInfo: any,
    relatedRelationshipInfo: any,
    relatedFeature: __esri.Graphic
  ): Observable<number> {
    const relationshipLayerUrl = this.getRelationshipLayerUrl(featureLayerUrl, relationshipInfo);

    // create where
    const fkFieldRelated: string = relatedRelationshipInfo.keyFieldInRelationshipTable;
    const fkFieldRelatedValue: string = relatedFeature.attributes[relatedRelationshipInfo.keyField];
    const where: string = fkFieldRelated + "='" + fkFieldRelatedValue + "'";

    // create query
    const relationshipQuery: __esri.Query = this._queryService.buildQuery(false, where);
    const errorCode = this._CONST.LAYER_ERROR.COUNT_FEATURES_FROM_LAYER_FAILED;
    // execute query
    return this._queryService.executeForCount(relationshipLayerUrl, relationshipQuery, errorCode);
  }

  /**
   * @name createRelationshipFeature
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param relatedRelationshipInfo
   * @param idFeature
   * @param idRelatedFeature
   * @returns {Observable<any>}
   * @description creates an new n-m relationship feature and saves it
   */
  public createRelationshipFeature(
    featureLayerUrl: string,
    relationshipInfo: any,
    relatedRelationshipInfo: any,
    idFeature: any,
    idRelatedFeature: any
  ): Observable<any> {
    const relationshipLayerUrl = this.getRelationshipLayerUrl(featureLayerUrl, relationshipInfo);

    // get keyField names
    const fkFieldFeature: string = relationshipInfo.keyFieldInRelationshipTable;
    const fkFieldRelatedFeature: string = relatedRelationshipInfo.keyFieldInRelationshipTable;

    // create new relationship feature (m-n table entry)
    const graphicProperties: __esri.GraphicProperties = { attributes: {} };
    const relationshipFeature: __esri.Graphic = new EsriGraphic(graphicProperties);
    relationshipFeature.attributes[fkFieldFeature] = idFeature;
    relationshipFeature.attributes[fkFieldRelatedFeature] = idRelatedFeature;

    // save relationship feature
    return this.applyEdits(relationshipLayerUrl, [relationshipFeature]);
  }

  /**
   * @name deleteRelationshipFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param relationshipFeatureIds
   * @returns {Observable<any>}
   * @description Deletes the relationship feature
   */
  public deleteRelationshipFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    relationshipFeatureIds: number[]
  ): Observable<any> {
    const relationshipLayerUrl = this.getRelationshipLayerUrl(featureLayerUrl, relationshipInfo);
    return this.applyEdits(relationshipLayerUrl, undefined, undefined, relationshipFeatureIds);
  }

  /**
   * @name deleteRelationshipFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param relatedFeatureIds
   * @returns {Observable<any>}
   * @description Deletes the related feature an his reationship-feature (if there is one)
   * will be deleted automatically
   */
  public deleteRelatedFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    relatedFeatureIds: number[]
  ): Observable<any> {
    const relatedLayerUrl = this.getRelatedLayerUrl(featureLayerUrl, relationshipInfo);
    return this.applyEdits(relatedLayerUrl, undefined, undefined, relatedFeatureIds);
  }

  /**
   * @name saveRelatedFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param adds
   * @param updates
   * @param deletes
   * @returns {Observable<any>}
   */
  public saveRelatedFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    adds?: __esri.Graphic[],
    updates?: __esri.Graphic[],
    deletes?: number[]
  ): Observable<any> {
    const relatedLayerUrl = this.getRelatedLayerUrl(featureLayerUrl, relationshipInfo);
    return this.applyEdits(relatedLayerUrl, adds, updates, deletes);
  }

  /**
   * @name searchRelatedFeatures
   * @param featureLayerUrl
   * @param relationshipInfo
   * @param searchField
   * @param idField
   * @param token
   * @returns {Observable<any>}
   * @description Searches the related table
   */
  public searchRelatedFeatures(
    featureLayerUrl: string,
    relationshipInfo: any,
    searchField: string,
    idField: string,
    token: string
  ): Observable<any> {
    const relatedLayerUrl = this.getRelatedLayerUrl(featureLayerUrl, relationshipInfo);

    // define query
    const where: string = 'LOWER(' + searchField + ") like '%" + token.toLocaleLowerCase() + "%'";
    const query: __esri.Query = this._queryService.buildQuery(false, where, [idField, searchField]);
    query.num = 20;
    const errorCode = this._CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED;
    // execute query
    return this._queryService.execute(relatedLayerUrl, query, errorCode);
  }

  /**
   * @name getRelatedLayerUrl
   * @param featureLayerUrl
   * @param relationshipInfo
   * @returns {string}
   */
  public getRelatedLayerUrl(featureLayerUrl: string, relationshipInfo: any): string {
    return this.createLayerUrl(featureLayerUrl, relationshipInfo.relatedTableId);
  }

  /**
   * @name getRelationshipLayerUrl
   * @param featureLayerUrl
   * @param relationshipInfo
   * @returns {string}
   */
  public getRelationshipLayerUrl(featureLayerUrl: string, relationshipInfo: any): string {
    return this.createLayerUrl(featureLayerUrl, relationshipInfo.relationshipTableId);
  }

  /**
   * @name getNewNumberForField
   * @returns {Observable<any>}
   * @description Queries the max number of the given staticField and return
   * a new number, which is max number + 1
   */
  public getNewNumberForField(
    relatedFeaturerUrl: string,
    staticField: string,
    staticType: string
  ): Observable<number> {
    return this.queryRelatedFeatureStatistic(
      relatedFeaturerUrl,
      staticType,
      staticField,
      'MAX_FIELD'
    ).pipe(
      map(maxProjectNr => {
        return maxProjectNr.features[0].attributes.MAX_FIELD + 1;
      })
    );
  }

  /**
   * @name queryRelatedFeatureStatistic
   * @param featureLayerUrl
   * @param featureLayerUrl
   * @param staticType
   * @param staticField
   * @param outStaticField
   * @description Get data by feature from related table
   */
  public queryRelatedFeatureStatistic(
    featureLayerUrl: string,
    staticType: string,
    staticField: any,
    outStaticField: string
  ): Observable<any> {
    // create query
    const relationshipQuery: __esri.Query = this._queryService.buildStatisticQuery(
      staticType,
      staticField,
      outStaticField
    );
    const errorCode = ErrorHandlingService.LayerError.GET_FEATURES_FROM_RELATION_TABLE;
    // execute query
    return this._queryService.execute(featureLayerUrl, relationshipQuery, errorCode);
  }

  /**
   * @name applyEdits
   * @param layerUrl
   * @param adds
   * @param updates
   * @param deletes
   * @returns {any}
   * @description The applyEdit function for Tables is in "ArcGis Js API 4.6" not yet suppported
   * ("The table feature service type is not yet supported") do save edits over the "ArcGis REST API"
   * The adds, updates and deletes sould be features
   */
  private applyEdits(
    layerUrl: string,
    adds?: __esri.Graphic[],
    updates?: __esri.Graphic[],
    deletes?: number[]
  ): Observable<any> {
    const a: any[] = this.convertToJsonList(adds);
    const u: any[] = this.convertToJsonList(updates);
    const d: number[] = deletes ? deletes : [];

    // define request parameters
    const applyEditUrl = layerUrl + '/applyEdits';
    const options = {
      method: 'post',
      query: {
        adds: JSON.stringify(a),
        updates: JSON.stringify(u),
        deletes: JSON.stringify(d),
        f: 'json',
      },
      responseType: 'json',
    } as __esri.RequestOptions;

    return from(
      EsriRequest(applyEditUrl, options)
        .then((result: any) => {
          return result;
        })
        .catch(error => {
          this._messageService.error('NOTIFICATIONS.SAVE_FAILED');
        })
    );
  }

  /**
   * @param list
   * @returns {any}
   * @description converts an array of graphics to an array of JSON from a graphic (graphic.toJSON()
   */
  private convertToJsonList(list?: __esri.Graphic[]): any[] {
    const l = list ? list : [];
    return l.map((m: __esri.Graphic) => m.toJSON());
  }

  /**
   * @name createLayerUrl
   * @param sourceFeatureLayerUrl
   * @param layerId
   * @returns {string}
   * @description Creates an Url with the basePath from source-url and layer-id
   */
  private createLayerUrl(sourceFeatureLayerUrl: string, layerId: string): string {
    const baseUrl: string = sourceFeatureLayerUrl.substr(
      0,
      sourceFeatureLayerUrl.lastIndexOf('/') + 1
    );
    return baseUrl + layerId;
  }
}
